import '@/styles/cupons-layout.css';
import { Link } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SimpleMenu } from '@/components/MenuGrid';
import { useLocale } from '@/context/LocaleContext';
import { resolveMediaUrl } from '@/lib/assetUrl';
import {
  useClaimCupon,
  useCupons,
  useRequestCuponCode,
  type CuponReward,
  type CuponRow,
} from '@/hooks/useCupons';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SEC = 60;

function formatEndDate(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function useCountdown(expiresAt: string | null, active: boolean) {
  const [label, setLabel] = useState('');
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!active || !expiresAt) {
      setLabel('');
      setExpired(false);
      return;
    }

    const expiresMs = new Date(expiresAt).getTime();

    const tick = () => {
      const distance = expiresMs - Date.now();
      if (distance < 0) {
        setLabel('EXPIRADO');
        setExpired(true);
        return;
      }
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);
      setLabel(`${minutes}m ${seconds}s`);
      setExpired(false);
    };

    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [active, expiresAt]);

  return { label, expired };
}

function useResendCooldown(initialActive: boolean) {
  const [secondsLeft, setSecondsLeft] = useState(initialActive ? RESEND_COOLDOWN_SEC : 0);

  const start = useCallback(() => {
    setSecondsLeft(RESEND_COOLDOWN_SEC);
  }, []);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = window.setInterval(() => {
      setSecondsLeft((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [secondsLeft]);

  return { secondsLeft, start, canResend: secondsLeft <= 0 };
}

function RewardItem({ reward }: { reward: CuponReward }) {
  const imgSrc = reward.image_url ? resolveMediaUrl(reward.image_url) : '';
  return (
    <div className="cupons-reward-item">
      {imgSrc ? (
        <div className="cupons-reward-image">
          <img src={imgSrc} alt={reward.SN_Name} />
          <span className="cupons-quantity-badge">x{reward.Quantity}</span>
        </div>
      ) : null}
      <div className="cupons-reward-details">
        <span className="cupons-reward-name" title={reward.SN_Name}>{reward.SN_Name}</span>
      </div>
    </div>
  );
}

function CuponInfoCard({ cupon }: { cupon: CuponRow }) {
  const { t } = useLocale();
  return (
    <div className="cupons-info-card">
      <h2 className="cupons-name">{cupon.name}</h2>
      <div className="cupons-meta">
        <span className="cupons-meta-inline">
          <i className="ph-light ph-calendar-blank" />
          <span>{formatEndDate(cupon.end_date)}</span>
        </span>
        {cupon.level_required > 0 ? (
          <>
            <span className="cupons-meta-separator">|</span>
            <span className="cupons-meta-inline">
              <i className="ph-light ph-star" />
              <span>
                {t('cupon_level_required')} {cupon.level_required}
              </span>
            </span>
          </>
        ) : null}
      </div>
    </div>
  );
}

interface OtpFormProps {
  codeExpires: string | null;
  hasActiveCode: boolean;
  email: string;
  whatsapp?: string;
  deliveryChannel?: 'email' | 'whatsapp';
  onClaimSuccess: () => void;
}

function OtpForm({ codeExpires, hasActiveCode, email, whatsapp, deliveryChannel, onClaimSuccess }: OtpFormProps) {
  const { t } = useLocale();
  const [channel, setChannel] = useState<'email' | 'whatsapp'>(deliveryChannel === 'whatsapp' && whatsapp ? 'whatsapp' : email ? 'email' : 'whatsapp');
  const [sentChannel, setSentChannel] = useState(deliveryChannel ?? 'email');
  const available = channel === 'email' ? !!email : !!whatsapp;
  const claimMutation = useClaimCupon();
  const requestMutation = useRequestCuponCode();
  const [digits, setDigits] = useState<string[]>(() => Array(OTP_LENGTH).fill(''));
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showVerification, setShowVerification] = useState(hasActiveCode);
  const [requestSent, setRequestSent] = useState(false);
  const [localExpires, setLocalExpires] = useState<string | null>(codeExpires);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { secondsLeft, start: startResendCooldown, canResend } = useResendCooldown(hasActiveCode);
  const { label: countdownLabel, expired: countdownExpired } = useCountdown(
    localExpires,
    showVerification,
  );

  useEffect(() => {
    if (hasActiveCode) {
      setShowVerification(true);
      setLocalExpires(codeExpires);
      window.setTimeout(() => inputRefs.current[0]?.focus(), 0);
    }
  }, [hasActiveCode, codeExpires]);

  const hideMessages = () => {
    setErrorMsg('');
    setSuccessMsg('');
  };

  const handleRequestCode = async (isFirstTime: boolean) => {
    hideMessages();
    try {
      const result = await requestMutation.mutateAsync(channel);
      if (result.success) {
        setSentChannel(channel);
        setDigits(Array(OTP_LENGTH).fill(''));
        if (result.codeExpires) {
          setLocalExpires(result.codeExpires);
        }
        if (isFirstTime) {
          setRequestSent(true);
          window.setTimeout(() => {
            setShowVerification(true);
            setRequestSent(false);
            startResendCooldown();
            inputRefs.current[0]?.focus();
          }, 1500);
        } else {
          startResendCooldown();
          setSuccessMsg(t('cupon_code_sent_button'));
        }
      } else {
        setErrorMsg(result.message ?? t('cupon_request_error'));
      }
    } catch {
      setErrorMsg(t('cupon_request_error'));
    }
  };

  const updateDigit = (index: number, value: string) => {
    const digit = value.replace(/[^0-9]/g, '').slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });
    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/[^0-9]/g, '');
    if (pasted.length === OTP_LENGTH) {
      setDigits(pasted.split(''));
      inputRefs.current[OTP_LENGTH - 1]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = digits.join('');
    if (code.length !== OTP_LENGTH) {
      setErrorMsg(t('cupon_complete_code'));
      return;
    }

    hideMessages();
    try {
      const result = await claimMutation.mutateAsync(code);
      if (result.success) {
        setSuccessMsg(result.message ?? t('cupon_coupon_claimed'));
        window.setTimeout(onClaimSuccess, 2000);
      } else {
        setErrorMsg(result.message ?? t('cupon_processing_error'));
      }
    } catch {
      setErrorMsg(t('cupon_processing_error'));
    }
  };

  return (
    <section className="cupons-delivery-panel">
      <fieldset className="cupons-channel-options" aria-label={t('cupon_choose_channel')} disabled={requestMutation.isPending || claimMutation.isPending || requestSent}>
        <label className={`${channel === 'email' ? 'is-selected' : ''}${!email ? ' is-disabled' : ''}`}>
          <input type="radio" name="coupon-channel" value="email" checked={channel === 'email'} disabled={!email} onChange={() => setChannel('email')} /> Email
        </label>
        <label className={`${channel === 'whatsapp' ? 'is-selected' : ''}${!whatsapp ? ' is-disabled' : ''}`}>
          <input type="radio" name="coupon-channel" value="whatsapp" checked={channel === 'whatsapp'} disabled={!whatsapp} onChange={() => setChannel('whatsapp')} /> WhatsApp
        </label>
      </fieldset>
      {errorMsg && <div className="cupons-error-message" role="alert" style={{display:'block'}}>{errorMsg}</div>}
      {!showVerification ? (
        <div className="cupons-claim-section">
          <button
            type="button"
            className={`cupons-claim-btn${requestSent ? ' cupons-claim-btn-sent' : ''}`}
            disabled={!available || requestMutation.isPending || requestSent}
            onClick={() => void handleRequestCode(true)}
            style={
              requestSent
                ? { background: 'linear-gradient(135deg, #28a745 0%, #1e7e34 100%)' }
                : undefined
            }
          >
            {requestMutation.isPending ? (
              t('cupon_sending')
            ) : requestSent ? (
              <>
                <i className="ph-fill ph-check-circle" /> {t('cupon_code_sent_button')}
              </>
            ) : (
              <>
                <i className="ph-light ph-gift" /> {t('cupon_claim_button')}
              </>
            )}
          </button>
        </div>
      ) : null}

      {!whatsapp && <Link className="cupons-settings-link" to="/setting">{t('cupon_configure_contact')}</Link>}
      <div
        className="cupons-verification-card"
        style={{ display: showVerification ? 'block' : 'none' }}
      >
        <h3 className="cupons-verification-title">{t('cupon_verification_title')}</h3>
        <p className="cupons-claim-info">{t('cupon_sent_via', {channel: sentChannel === 'email' ? 'Email' : 'WhatsApp'})}</p>
        {localExpires ? (
          <p className="cupons-verification-expires">
            {t('cupon_expires_in')}:{' '}
            <span id="countdown-timer" style={countdownExpired ? { color: '#dc3545' } : undefined}>
              {countdownLabel}
            </span>
          </p>
        ) : null}

        <form className="cupons-form" onSubmit={(e) => void handleSubmit(e)}>
          <div className="cupon-code-input-group">
            {digits.map((digit, index) => (
              <span key={index} style={{ display: 'contents' }}>
                {index === 3 ? <div className="cupon-code-separator" /> : null}
                <input
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  type="text"
                  className="cupon-code-input"
                  maxLength={1}
                  aria-label={`${t('cupon_verification_title')} ${index + 1}`}
                  autoComplete={index === 0 ? "one-time-code" : "off"}
                  inputMode="numeric"
                  value={digit}
                  onChange={(e) => updateDigit(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                />
              </span>
            ))}
          </div>

          {successMsg ? (
            <div className="cupons-success-message" style={{ display: 'block' }}>
              {successMsg}
            </div>
          ) : null}

          <button
            type="submit"
            className="cupons-submit-btn"
            disabled={claimMutation.isPending || countdownExpired}
          >
            {claimMutation.isPending ? t('cupon_processing') : t('cupon_confirm_button')}
          </button>

          <button
            type="button"
            className="cupons-resend-btn"
            disabled={!available || !canResend || requestMutation.isPending}
            onClick={() => void handleRequestCode(false)}
          >
            {canResend
              ? t('cupon_resend_button')
              : `${t('cupon_resend_button')} (${secondsLeft}s)`}
          </button>
        </form>
      </div>
    </section>
  );
}

export function CuponsPage() {
  const { t } = useLocale();
  const { data, isLoading, refetch } = useCupons();

  return (
    <div className="container-ingame cupons-page">
      <div className="cupons-container">
        <SimpleMenu title={t('cupon_title')} />

        <div className="cupons-body">
          {isLoading ? (
            <div className="cupons-message cupons-message-info">...</div>
          ) : null}

          {!isLoading && !data?.cupon ? (
            <div
              className="alert alert-info"
              style={{ marginBottom: 20, padding: 15, borderRadius: 5, textAlign: 'center' }}
            >
              {data?.message ?? t('cupon_no_coupons')}
            </div>
          ) : null}

          {!isLoading && data?.cupon ? (
            <>
              <CuponInfoCard cupon={data.cupon} />

              {data.rewards.length > 0 ? (
                <div className="cupons-rewards">
                  <h3 className="cupons-rewards-title">{t('cupon_rewards_title')}</h3>
                  <div className="cupons-rewards-grid">
                    {data.rewards.map((reward) => (
                      <RewardItem key={reward.id} reward={reward} />
                    ))}
                  </div>
                </div>
              ) : null}

              {data.hasClaimed ? (
                <div className="cupons-claim-section">
                  <button type="button" className="cupons-claim-btn cupons-claim-btn-claimed" disabled>
                    <i className="fa-solid fa-circle-check checkin-icon-green" />{' '}
                    <span>{t('cupon_claimed_button')}</span>
                  </button>
                  <p className="cupons-claim-info">{t('cupon_already_claimed')}</p>
                </div>
              ) : (
                <OtpForm
                  codeExpires={data.codeExpires}
                  hasActiveCode={data.hasActiveCode}
                  email={data.email}
                  whatsapp={data.whatsapp}
                  deliveryChannel={data.deliveryChannel}
                  onClaimSuccess={() => void refetch()}
                />
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
